import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cog, ChevronDown, Beaker, Droplets, Thermometer, Zap, Wind, Atom, ShieldCheck, Layers, CheckCircle2, XCircle } from 'lucide-react';
import type { AssessmentResult } from '../hooks/useCalculator';

interface ProcessTrainDetailProps {
  result: AssessmentResult;
}

const safeNum = (n: number | undefined | null) => (typeof n === 'number' ? n : 0);

export default function ProcessTrainDetail({ result }: ProcessTrainDetailProps) {
  const [expandedStage, setExpandedStage] = useState<number | null>(0);
  const sim = result.simulation;
  if (!sim) return null;

  const getStageColor = (stageName: string) => {
    if (stageName.includes('Equalization')) return { bg: 'bg-cyan-500', text: 'text-cyan-400', soft: 'bg-cyan-500/10', border: 'border-cyan-500/20' };
    if (stageName.includes('Headworks')) return { bg: 'bg-slate-500', text: 'text-slate-400', soft: 'bg-slate-500/10', border: 'border-slate-500/20' };
    if (stageName.includes('DAF')) return { bg: 'bg-rb-brand', text: 'text-rb-brand', soft: 'bg-rb-brand/10', border: 'border-rb-brand/20' };
    if (stageName.includes('Coagulation')) return { bg: 'bg-orange-500', text: 'text-orange-400', soft: 'bg-orange-500/10', border: 'border-orange-500/20' };
    if (stageName.includes('Filter')) return { bg: 'bg-slate-500', text: 'text-slate-400', soft: 'bg-slate-500/10', border: 'border-slate-500/20' };
    if (stageName.includes('Biological')) return { bg: 'bg-emerald-500', text: 'text-emerald-400', soft: 'bg-emerald-500/10', border: 'border-emerald-500/20' };
    if (stageName.includes('GAC')) return { bg: 'bg-purple-500', text: 'text-purple-400', soft: 'bg-purple-500/10', border: 'border-purple-500/20' };
    if (stageName.includes('RO')) return { bg: 'bg-cyan-500', text: 'text-cyan-400', soft: 'bg-cyan-500/10', border: 'border-cyan-500/20' };
    if (stageName.includes('Polishing')) return { bg: 'bg-teal-500', text: 'text-teal-400', soft: 'bg-teal-500/10', border: 'border-teal-500/20' };
    if (stageName.includes('Sludge')) return { bg: 'bg-red-500', text: 'text-red-400', soft: 'bg-red-500/10', border: 'border-red-500/20' };
    return { bg: 'bg-cyan-500', text: 'text-cyan-400', soft: 'bg-cyan-500/10', border: 'border-cyan-500/20' };
  };

  return (
    <div className="space-y-5">
      <div className="text-center">
        <h2 className="text-sm font-bold text-white">Detailed Process Train</h2>
        <p className="text-xs text-slate-500">Stage-by-stage parameters, tank sizing, BOD/COD/TSS reduction, and pH evolution</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total BOD Reduction', value: `${(safeNum(sim.totalBodReduction) * 100).toFixed(1)}%`, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
          { label: 'Total COD Reduction', value: `${(safeNum(sim.totalCodReduction) * 100).toFixed(1)}%`, color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20' },
          { label: 'Total TSS Reduction', value: `${(safeNum(sim.totalTssReduction) * 100).toFixed(1)}%`, color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20' },
          { label: 'Final pH', value: safeNum(sim.finalPh).toFixed(2), color: 'text-rb-brand', bg: 'bg-rb-brand/10', border: 'border-rb-brand/20' },
        ].map((card, i) => (
          <div key={i} className={`p-3 rounded-xl border ${card.border} ${card.bg}`}>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">{card.label}</div>
            <div className={`text-xl font-bold ${card.color} mt-1`}>{card.value}</div>
          </div>
        ))}
      </div>

      {/* Tank Inventory */}
      <div className="space-y-2">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
          Tank & Equipment Inventory <span className="text-slate-600 font-normal">({sim.totalTanks.length} units, {safeNum(sim.totalTankVolumeM3).toLocaleString()} m³ total)</span>
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-700 bg-slate-800/30">
                <th className="text-left text-slate-400 font-bold pb-2 pr-4 text-[10px] uppercase">Equipment</th>
                <th className="text-left text-slate-400 font-bold pb-2 pr-4 text-[10px] uppercase">Type</th>
                <th className="text-left text-slate-400 font-bold pb-2 pr-4 text-[10px] uppercase">Purpose</th>
                <th className="text-right text-slate-400 font-bold pb-2 pr-4 text-[10px] uppercase">Volume</th>
                <th className="text-left text-slate-400 font-bold pb-2 pr-4 text-[10px] uppercase">Dimensions</th>
                <th className="text-left text-slate-400 font-bold pb-2 text-[10px] uppercase">Notes</th>
              </tr>
            </thead>
            <tbody>
              {sim.totalTanks.map((tank, i) => (
                <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                  <td className="py-2 pr-4 font-bold text-white text-xs">{tank.name}</td>
                  <td className="py-2 pr-4 text-slate-400">{tank.type}</td>
                  <td className="py-2 pr-4 text-slate-400">{tank.purpose}</td>
                  <td className="py-2 pr-4 text-right font-mono text-slate-300">{tank.volumeM3} m³</td>
                  <td className="py-2 pr-4 text-slate-400">{tank.dimensions}</td>
                  <td className="py-2 text-slate-500">{tank.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stage Detail Accordion */}
      <div className="space-y-2">
        {sim.stages.map((stage, i) => {
          const colors = getStageColor(stage.stageName);
          const isExpanded = expandedStage === i;
          return (
            <div key={i} className={`rounded-xl border ${isExpanded ? colors.border : 'border-slate-800/50'} overflow-hidden transition-all`}>
              <button
                onClick={() => setExpandedStage(isExpanded ? null : i)}
                className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-800/20 transition-colors"
              >
                <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center text-white text-xs font-bold`}>
                  {stage.stageNumber}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-white">{stage.stageName}</div>
                  <div className="text-[10px] text-slate-500">{stage.equipment.map(e => e.name).join(' + ')}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] text-slate-500">{stage.tanks.length} tanks</span>
                  <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
              </button>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <div className="px-3 pb-3 pt-1 border-t border-slate-800/50 space-y-3">
                      {/* Parameters */}
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                        {[
                          { label: 'BOD', value: stage.bod5, unit: 'mg/L', color: 'text-emerald-400' },
                          { label: 'COD', value: stage.cod, unit: 'mg/L', color: 'text-cyan-400' },
                          { label: 'TSS', value: stage.tss, unit: 'mg/L', color: 'text-cyan-400' },
                          { label: 'pH', value: stage.ph, unit: '', color: 'text-rb-brand', dec: 2 },
                          { label: 'TDS', value: stage.tds, unit: 'mg/L', color: 'text-slate-300' },
                          { label: 'O&G', value: stage.oilGrease, unit: 'mg/L', color: 'text-rb-brand' },
                          { label: 'TKN', value: stage.tkn ?? (safeNum(stage.totalNitrogen) * 0.85), unit: 'mg/L', color: 'text-slate-300' },
                          { label: 'TN', value: stage.totalNitrogen, unit: 'mg/L', color: 'text-slate-300' },
                          { label: 'TP', value: stage.totalPhosphorus, unit: 'mg/L', color: 'text-slate-300' },
                          { label: 'Temp', value: stage.temperature, unit: '°C', color: 'text-slate-300', dec: 1 },
                          { label: 'Conductivity', value: stage.conductivity, unit: 'µS/cm', color: 'text-slate-300' },
                          { label: 'BOD↓', value: `${(safeNum(stage.bodReductionSoFar) * 100).toFixed(1)}%`, unit: '', color: 'text-emerald-400', raw: true },
                        ].map((param, pi) => (
                          <div key={pi} className="p-2 rounded-lg bg-slate-800/30 border border-slate-800/50 text-center">
                            <div className="text-[10px] text-slate-500 uppercase font-bold">{param.label}</div>
                            <div className={`text-sm font-bold ${param.color}`}>
                              {param.raw ? param.value : safeNum(param.value as number).toFixed(param.dec ?? 0)}{param.unit ? ` ${param.unit}` : ''}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Tanks */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {stage.tanks.map((tank, ti) => (
                          <div key={ti} className="p-2.5 rounded-lg bg-slate-800/30 border border-slate-800/50">
                            <div className="text-xs font-bold text-white">{tank.name}</div>
                            <div className="text-[10px] text-slate-500">{tank.volumeM3} m³ | {tank.hrt} hr HRT</div>
                            <div className="text-[10px] text-slate-600">{tank.dimensions}</div>
                          </div>
                        ))}
                      </div>

                      {/* Chemicals */}
                      {stage.chemicals.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {stage.chemicals.map((chem, ci) => (
                            <span key={ci} className="px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-[10px] text-slate-400">
                              {chem.name}: {chem.dose} {chem.unit}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}
